import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { translateTo, useT, type Locale, type T } from "@/i18n";
import data from "@/data/kitChecklists.json";
import { CARTON_LOGO } from "@/lib/cartonLogo";

// His "📁 Documents & checklists" (Z Production): the printable carton checklist of every kit type —
// even a kit with no production order running — and his reference PDFs. The checklists are HIS data
// (kitChecklists.json, evaluated from his artifact, not retyped); the sheet copies the A4 checklist
// slipped into each carton: kit band, one row per article with quantity, code and FR/EN names, the
// linen phases, and the "Carton complet et vérifié" footer. His sheet is French with English
// underneath; here it is the reader's language with English underneath (English alone for an
// English reader), so Ludovic's sheet is unchanged and Berlin's reads German / English.
type Item = { code: string; name: string; nameEn?: string; nameDe?: string; nameIt?: string; qty: number };
type Phases = { installed: Item[]; reserve: Item[]; note?: string; noteEn?: string; noteDe?: string; noteIt?: string };
type Kit = {
  key: string; label: string; color: string; textColor: string; composedOf: string[] | null; phases: Phases | null;
  groups: { key: string; label: string; color: string; textColor: string; items: Item[] }[] | null;
  items: Item[] | null; total: number;
};
const KITS = (data as { kits: Kit[] }).kits;

// His reference documents, the PDFs already printed and slipped into cartons / shown on the shelves.
const REFERENCE_DOCS = [
  { icon: "📄", key: "docs.ref.checklists", file: "Checklists_Kits_Myotaku.pdf" },
  { icon: "🏷️", key: "docs.ref.shelf", file: "Etiquettes_Etageres_Myotaku.pdf" },
  { icon: "📋", key: "docs.ref.inventory", file: "Inventaire_General_Myotaku.pdf" },
];

// The article's name in each language; his data carries French and English, the other two were
// added beside them.
const itemName = (it: Item, l: Locale) => ({ fr: it.name, en: it.nameEn, de: it.nameDe, it: it.nameIt }[l] || it.name);
const noteText = (p: Phases, l: Locale) => ({ fr: p.note, en: p.noteEn, de: p.noteDe, it: p.noteIt }[l] || p.note);
// The English line under the reader's own, as on his sheet (nothing for an English reader).
const second = (l: Locale, text: string | undefined) => (l === "en" ? undefined : text);

export default function KitChecklists() {
  const { t } = useT();
  // His Documents card opens with every sheet ticked.
  const [selected, setSelected] = useState<Set<string>>(() => new Set(KITS.map((k) => k.key)));
  const [printing, setPrinting] = useState(false);
  const toggle = (k: string) => setSelected((s) => { const n = new Set(s); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const print = () => setPrinting(true);
  const chosen = KITS.filter((k) => selected.has(k.key));

  return (
    <div className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-slate-900">📄 {t("docs.refTitle")}</h3>
        <p className="mb-2 text-xs text-slate-500">{t("docs.refHint")}</p>
        <div className="flex flex-col gap-2">
          {REFERENCE_DOCS.map((d) => (
            <a key={d.file} href={`/reference/${d.file}`} target="_blank" rel="noreferrer"
               className="flex items-center gap-3 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 hover:border-orange-400">
              <span className="text-xl">{d.icon}</span>
              <span className="min-w-0 flex-1"><b className="block text-sm text-slate-900">{t(`${d.key}.title`)}</b><span className="text-xs text-slate-500">{t(`${d.key}.desc`)}</span></span>
              <span className="text-xs font-semibold text-orange-700">{t("docs.open")}</span>
            </a>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button onClick={() => setSelected(new Set(KITS.map((k) => k.key)))} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("docs.selectAll")}</button>
        <button onClick={() => setSelected(new Set())} className="rounded-lg px-3 py-1.5 text-sm ring-1 ring-slate-300">{t("docs.selectNone")}</button>
        <button onClick={print} disabled={!chosen.length} className="rounded-lg bg-orange-600 px-3 py-1.5 text-sm text-white disabled:opacity-40">
          🖨 {t("docs.print").replace("{n}", String(chosen.length))}
        </button>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {KITS.map((k) => (
          <div key={k.key} className="overflow-hidden rounded-xl border border-slate-200 bg-white">
            <label className="flex cursor-pointer items-center gap-2 border-b border-slate-100 px-3 py-2 text-sm font-medium text-slate-600">
              <input type="checkbox" checked={selected.has(k.key)} onChange={() => toggle(k.key)} />
              {t("docs.select")}
            </label>
            <Sheet kit={k} />
          </div>
        ))}
      </div>

      {printing && <PrintSheets kits={chosen} onDone={() => setPrinting(false)} />}
    </div>
  );
}

// The rows of a kit's sheet, all groups and phases together — his `nRows`, which decides the dense layout.
const rowCount = (k: Kit) => (k.groups?.reduce((n, g) => n + g.items.length, 0) ?? 0)
  + (k.phases ? k.phases.installed.length + k.phases.reserve.length : 0) + (k.items?.length ?? 0);

// His usable A4 height (190 × 277 mm at 10 mm margins) in CSS pixels.
const PAGE_HEIGHT = 1040;

// His `printKits`: every sheet on its own A4 page (`copies` of each — his carton sheets of an order);
// a sheet of more than 22 rows in two columns, and one still taller than the page shrunk just enough
// to fit. The sheets are laid out off screen at the page's width, measured, then printed.
function PrintSheets({ kits, copies = 1, onDone }: { kits: Kit[]; copies?: number; onDone: () => void }) {
  const box = useRef<HTMLDivElement>(null);
  const done = useRef(onDone);
  done.current = onDone;
  useEffect(() => {
    const id = setTimeout(() => {
      box.current?.querySelectorAll<HTMLElement>(".kchk-sheet").forEach((sheet) => {
        const h = sheet.getBoundingClientRect().height;
        if (h > PAGE_HEIGHT) sheet.style.zoom = (PAGE_HEIGHT / h).toFixed(3);
      });
      window.print();
      done.current();
    }, 50);
    return () => clearTimeout(id);
  }, []);
  return createPortal(
    <div ref={box} className="print-checklists" aria-hidden="true">
      {kits.flatMap((k) => Array.from({ length: copies }, (_, i) => <Sheet key={`${k.key}-${i}`} kit={k} />))}
    </div>,
    document.body,
  );
}

// His per-order "🖨️ Imprimer n fiches carton": one checklist page per kit of the order, to slip into
// each carton (an order of 10 office kits = 10 sheets).
export function CartonSheetsButton({ kitCode, qty }: { kitCode: string; qty: number }) {
  const { t } = useT();
  const [printing, setPrinting] = useState(false);
  const kit = KITS.find((k) => k.key === kitCode);
  if (!kit || qty <= 0) return null;
  return (
    <>
      <button onClick={() => setPrinting(true)} disabled={printing} className="rounded border border-slate-300 px-2.5 py-1 text-xs disabled:opacity-40">
        🖨️ {qty > 1 ? t("prod.printSheets", { n: qty }) : t("prod.printSheet")}
      </button>
      {printing && <PrintSheets kits={[kit]} copies={Math.min(200, qty)} onDone={() => setPrinting(false)} />}
    </>
  );
}

function Rows({ items, color }: { items: Item[]; color: string }) {
  const { locale } = useT();
  return (
    <div className="kchk-rows">
      {items.map((it, i) => (
        <div key={`${it.code}-${i}`} className={`kchk-row flex items-center gap-2.5 px-4 py-1.5 text-[12.5px] ${i % 2 ? "bg-slate-50" : ""}`}>
          <span className="h-4 w-4 shrink-0 rounded border-2 border-slate-400" />
          <span className={`w-9 shrink-0 text-center font-bold ${it.qty > 1 ? "kchk-band rounded-full text-white" : "text-slate-700"}`}
                style={it.qty > 1 ? { background: color } : undefined}>×{it.qty}</span>
          <span className="w-16 shrink-0 font-mono text-xs font-bold" style={{ color }}>{it.code}</span>
          <span className="min-w-0"><span className="font-medium text-slate-900">{itemName(it, locale)}</span>
            {second(locale, it.nameEn) && <span className="ml-1.5 text-xs italic text-slate-500">{it.nameEn}</span>}</span>
        </div>
      ))}
    </div>
  );
}

function Bar({ main, en, color, textColor }: { main: string; en?: string; color: string; textColor: string }) {
  return (
    <div className="kchk-band flex items-baseline justify-between gap-2 px-4 py-1.5" style={{ background: color, color: textColor }}>
      <span className="text-xs font-extrabold uppercase tracking-wide">{main}</span>
      {en && <span className="text-[10.5px] italic opacity-85">{en}</span>}
    </div>
  );
}

// "Main line / English line", the English part only when the reader is not already reading English.
function Both({ t, k, l, className }: { t: T; k: string; l: Locale; className?: string }) {
  return <>{t(k)}{l !== "en" && <span className={className ?? "italic opacity-85"}> / {translateTo("en", k)}</span>}</>;
}

function Sheet({ kit }: { kit: Kit }) {
  const { t, locale } = useT();
  return (
    <div className={`kchk-sheet bg-white ${rowCount(kit) > 22 ? "kchk-dense" : ""}`}>
      <div className="kchk-band flex items-center gap-3 px-4 py-3" style={{ background: kit.color, color: kit.textColor }}>
        <img src={CARTON_LOGO} alt="Myotaku" className="h-7 w-10 shrink-0 rounded-[5px] bg-white object-cover" />
        <div>
          <div className="text-base font-extrabold leading-tight">{kit.label.toUpperCase()}</div>
          <div className="text-[11px] font-semibold opacity-90"><Both t={t} k="chk.title" l={locale} /></div>
        </div>
      </div>
      {kit.groups && kit.groups.map((g) => (
        <div key={g.key}><Bar main={g.label.toUpperCase()} color={g.color} textColor={g.textColor} /><Rows items={g.items} color={g.color} /></div>
      ))}
      {kit.phases && (
        <>
          <Bar main={t("chk.phase1").toUpperCase()} en={second(locale, translateTo("en", "chk.phase1Short"))} color={kit.color} textColor={kit.textColor} />
          <Rows items={kit.phases.installed} color={kit.color} />
          <Bar main={t("chk.phase2").toUpperCase()} en={second(locale, translateTo("en", "chk.phase2Short"))} color={kit.color} textColor={kit.textColor} />
          <Rows items={kit.phases.reserve} color={kit.color} />
          {kit.phases.note && (
            <div className="flex items-center gap-2.5 bg-slate-100 px-4 py-2 text-[12.5px]">
              <span className="h-4 w-4 shrink-0 rounded border-2 border-slate-400" />
              <span><b>{noteText(kit.phases, locale)}</b>{second(locale, kit.phases.noteEn) && <span className="italic text-slate-500"> / {kit.phases.noteEn}</span>}</span>
            </div>
          )}
        </>
      )}
      {kit.items && <Rows items={kit.items} color={kit.color} />}
      <div className="space-y-2 border-t border-slate-200 px-4 py-3 text-[12px]">
        <label className="flex items-center gap-2 font-semibold"><span className="h-4 w-4 rounded border-2 border-slate-400" />
          <Both t={t} k="chk.complete" l={locale} className="font-normal italic text-slate-500" /></label>
        <div className="grid grid-cols-2 gap-4 text-slate-500">
          <div>{t("chk.preparedBy")} / {t("chk.date")}<div className="mt-4 border-b border-slate-400" /></div>
          <div>{t("chk.checkedBy")} / {t("chk.signature")}<div className="mt-4 border-b border-slate-400" /></div>
        </div>
        <div className="text-[10.5px] text-slate-400">Myotaku SA — myotaku.ch · {t(kit.total > 1 ? "chk.articles" : "chk.article", { n: kit.total })}</div>
      </div>
    </div>
  );
}
